import { useEffect, useRef } from "react";

import { useSheet } from "@/shared/components/sheet";

import { PluginDrawer } from "./plugin-drawer";
import { usePluginManager } from "./plugin-manager-context";
import { PluginModal } from "./plugin-modal";
import { getPluginDrawerSheetKey } from "../lib/plugin-panel-key";
import { usePluginStore } from "../state/plugin-store";

/**
 * 全局面板宿主：订阅 `plugin-store` 中由 RPC `ui.openPanel` 写入的状态，
 * modal 在宿主侧受控渲染，drawer 请求桥接到统一的命令式 Sheet。
 */
export function PluginPanelHost(): React.ReactElement | null {
  const { open: openSheet } = useSheet();
  const openPanel = usePluginStore((s) => s.openPanel);
  const closePanel = usePluginStore((s) => s.closePanel);
  const manager = usePluginManager();
  const handledDrawerRef = useRef<typeof openPanel>(null);

  useEffect(() => {
    if (!openPanel || openPanel.display !== "drawer" || !manager) {
      return;
    }

    if (handledDrawerRef.current === openPanel) {
      return;
    }

    const baseUrl = manager.getBaseUrl(openPanel.pluginId);
    if (!baseUrl) {
      return;
    }

    handledDrawerRef.current = openPanel;
    const request = openPanel;
    const src = manager.resolveUrl(baseUrl, request.path);
    const title = manager.getManifest(request.pluginId)?.name ?? "插件";

    void openSheet(PluginDrawer, {
      pluginId: request.pluginId,
      src,
      title,
    }, {
      key: getPluginDrawerSheetKey(request.pluginId),
      className: "flex flex-col overflow-hidden p-0",
      style: { width: request.width ?? 360, maxWidth: "100vw" },
    }).finally(() => {
      if (usePluginStore.getState().openPanel === request) {
        closePanel();
      }
    });
  }, [closePanel, manager, openPanel, openSheet]);

  if (!openPanel || !manager) return null;

  const baseUrl = manager.getBaseUrl(openPanel.pluginId);
  if (!baseUrl) return null;

  const src = manager.resolveUrl(baseUrl, openPanel.path);
  const manifest = manager.getManifest(openPanel.pluginId);
  const title = manifest?.name ?? "插件";

  if (openPanel.display === "drawer") {
    return null;
  }

  // modal 和 popup 都使用 Modal（popup 需要锚点，openPanel 场景没有触发按钮）
  return (
    <PluginModal
      open
      onOpenChange={(open) => { if (!open) closePanel(); }}
      pluginId={openPanel.pluginId}
      src={src}
      title={title}
      width={openPanel.width}
      height={openPanel.height}
    />
  );
}
